/**
 * Layout of a result tab (layout lab, lot 2b, 03/10/2026). A section may say
 * how wide it is; the code places the sections in rows of 12 columns, in the
 * order of the composition. A row never ends with a hole: its last section
 * stretches. Phones always stack the sections (the renderer's grid starts at
 * the large breakpoint). A section without width takes the full row, so tools
 * designed before this keep their look.
 */

export const RESULT_SECTION_WIDTHS = ['full', 'wide', 'half', 'narrow'] as const;
export type ResultSectionWidth = typeof RESULT_SECTION_WIDTHS[number];

export const RESULT_LAYOUT_COLUMNS = 12;

const SPAN: Record<ResultSectionWidth, number> = { full: 12, wide: 8, half: 6, narrow: 4 };

export const isResultSectionWidth = (value: unknown): value is ResultSectionWidth =>
  typeof value === 'string' && RESULT_SECTION_WIDTHS.includes(value as ResultSectionWidth);

export interface SectionPlacement {
  row: number;
  col: number;
  colSpan: number;
}

export const placeSections = (widths: ReadonlyArray<ResultSectionWidth | undefined>): SectionPlacement[] => {
  const placements: SectionPlacement[] = [];
  let row = 0;
  let cursor = 0;
  const closeRow = () => {
    const last = placements.at(-1);
    if (last && last.row === row && cursor < RESULT_LAYOUT_COLUMNS) last.colSpan += RESULT_LAYOUT_COLUMNS - cursor;
    row += 1;
    cursor = 0;
  };
  for (const width of widths) {
    const span = SPAN[width ?? 'full'];
    if (cursor > 0 && cursor + span > RESULT_LAYOUT_COLUMNS) closeRow();
    placements.push({ row, col: cursor, colSpan: span });
    cursor += span;
    if (cursor >= RESULT_LAYOUT_COLUMNS) closeRow();
  }
  if (cursor > 0) closeRow();
  return placements;
};
